import { isFilterValid } from "../filter/utils/filterValidation";

export type ObjectSortOrder = "asc" | "desc";

const isObject = (value: any) => value && typeof value === "object" && !Array.isArray(value);
const OBJECT_TYPE_PATTERN = /^[a-z0-9_]+$/;
const FIELD_PATTERN = /^[a-zA-Z0-9_]+$/;
const toSafeField = (field?: string) => String(field || "")
  .replace(/[^a-zA-Z0-9_.]/g, "")
  .trim();

const unsupportedFieldPaths = new Set<string>([
  "accounts.tracking_domain",
]);

export const isUnsupportedObjectField = (field?: string) => (
  unsupportedFieldPaths.has(toSafeField(field).toLowerCase())
);

export const normalizeObjectEntityType = (entityType?: string): string => {
  const normalized = String(entityType || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "");
  if (!normalized) return "";
  if (normalized === "contacts") return "users";
  return normalized;
};

export const isMetricLikeField = (field?: string) => {
  const normalized = String(field || "");
  return normalized.startsWith("default_metric_")
    || normalized.startsWith("metric_")
    || normalized.includes(".default_metric_")
    || normalized.includes(".metric_");
};

export const stripEntityPrefix = (field?: string) => {
  const safe = toSafeField(field);
  if (!safe) return "";

  const firstDotIndex = safe.indexOf(".");
  const withoutPrefix = firstDotIndex >= 0 ? safe.slice(firstDotIndex + 1) : safe;
  return withoutPrefix.replace(/\./g, "_");
};

const toSafeObjectField = (field?: string) => {
  const normalized = stripEntityPrefix(field);
  if (!normalized || !FIELD_PATTERN.test(normalized)) return "";
  return normalized;
};

const clampNumber = (
  value: any,
  min: number,
  max: number,
  fallback: number,
) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(parsed)));
};

const toObjectSortOrder = (
  orderOrDirection?: any,
  fallback: ObjectSortOrder = "desc",
): ObjectSortOrder => {
  if (!orderOrDirection) return fallback;
  const normalized = String(orderOrDirection).toLowerCase();
  if (normalized === "asc" || normalized === "ascend") return "asc";
  if (normalized === "desc" || normalized === "descend") return "desc";
  return fallback;
};

const normalizeRelativeTime = (value: any) => {
  if (typeof value !== "string") return value;
  const normalized = value.trim().toLowerCase().replace(/\s+/g, "_");
  const mapped = normalized.match(/^(last|next)_(\d+)_days?$/);
  if (mapped) return `${mapped[1]}_${mapped[2]}_days`;

  if (normalized === "today") return "last_1_days";
  if (normalized === "yesterday") return "last_1_days";

  return normalized;
};

const toRelativeBoundaryToken = (value: any, operator: "before" | "after") => {
  const normalized = normalizeRelativeTime(value);
  if (typeof normalized !== "string") return "$now";
  const mapped = normalized.match(/^(last|next)_(\d+)_days$/);
  if (!mapped) return "$now";

  const direction = mapped[1];
  const days = mapped[2];

  if (operator === "before") {
    return direction === "next" ? `$now+${days}d` : "$now";
  }

  return direction === "last" ? `$now-${days}d` : "$now";
};

const mapOperator = (operator?: string) => {
  const normalized = String(operator || "").toLowerCase();
  switch (normalized) {
    case "eq":
    case "neq":
    case "gt":
    case "gte":
    case "lt":
    case "lte":
    case "in":
    case "not_in":
    case "between":
    case "relative":
    case "contains":
    case "not_contains":
    case "starts_with":
    case "ends_with":
    case "is_known":
    case "is_unknown":
      return normalized;
    case "ne":
      return "neq";
    case "contain":
      return "contains";
    case "does_not_contain":
      return "not_contains";
    case "known":
      return "is_known";
    case "unknown":
      return "is_unknown";
    case "range":
      return "between";
    case "on":
      return "eq";
    default:
      return "";
  }
};

function toObjectFilterNode(node: any, insideRelated = false, queryEntityType = ""): Record<string, any> | null {
  if (!isObject(node)) return null;

  if (Array.isArray(node.$and)) {
    const children = node.$and.map((child: any) => toObjectFilterNode(child, insideRelated, queryEntityType)).filter(Boolean);
    if (!children.length) return null;
    if (children.length === 1) return children[0];
    return { $and: children };
  }

  if (Array.isArray(node.$or)) {
    const children = node.$or.map((child: any) => toObjectFilterNode(child, insideRelated, queryEntityType)).filter(Boolean);
    if (!children.length) return null;
    if (children.length === 1) return children[0];
    return { $or: children };
  }

  if (isObject(node.$not)) {
    const child = toObjectFilterNode(node.$not, insideRelated, queryEntityType);
    return child ? { $not: child } : null;
  }

  if (node.$related || node.$not_related) {
    if (insideRelated) return null;
    const isNegative = !!node.$not_related;
    const relatedNode = node.$related || node.$not_related;
    if (!isObject(relatedNode)) return null;

    const relatedType = normalizeObjectEntityType(relatedNode.object_type);
    if (!relatedType || !OBJECT_TYPE_PATTERN.test(relatedType)) return null;

    if (isNegative) {
      return { $not_related: { object_type: relatedType } };
    }

    const relatedFilter = relatedNode.filter
      ? toObjectFilterNode(relatedNode.filter, true, queryEntityType)
      : null;

    return {
      $related: {
        object_type: relatedType,
        ...(relatedFilter ? { filter: relatedFilter } : {}),
      },
    };
  }

  if (Array.isArray(node.predicates)) {
    const children = node.predicates
      .map((predicate: any) => toObjectFilterNode(predicate, insideRelated, queryEntityType))
      .filter(Boolean);

    if (!children.length) return null;
    if (children.length === 1) return children[0];

    return String(node.query_type || "AND").toUpperCase() === "OR"
      ? { $or: children }
      : { $and: children };
  }

  const rawField = node.field;
  const rawOperator = String(node.op || node.operator || "").toLowerCase();

  if (!rawField || !rawOperator) {
    return null;
  }

  if (!node.op && !isFilterValid(rawField, rawOperator, node.value)) {
    return null;
  }

  if (node.entity_type && node.entity_type !== "column") {
    return null;
  }

  if (isUnsupportedObjectField(rawField)) {
    return null;
  }

  // Detect cross-object field references (e.g. "accounts.external_id" when querying users)
  // and convert them to $related filters for the backend API.
  // Skip self-relations (e.g. "accounts.name" when querying accounts) — treat as regular fields.
  const safeRaw = toSafeField(rawField);
  const dotIndex = safeRaw.indexOf(".");
  if (dotIndex > 0 && !insideRelated) {
    const relatedObjectType = normalizeObjectEntityType(safeRaw.slice(0, dotIndex));
    const relatedField = safeRaw.slice(dotIndex + 1);
    const isSelfRelation = queryEntityType && relatedObjectType === normalizeObjectEntityType(queryEntityType);
    if (!isSelfRelation && relatedObjectType && OBJECT_TYPE_PATTERN.test(relatedObjectType) && relatedField && FIELD_PATTERN.test(relatedField)) {
      const mappedOp = mapOperator(rawOperator);
      if (!mappedOp) return null;

      const relatedLeaf: Record<string, any> = { field: relatedField, op: mappedOp };
      if (mappedOp !== "is_known" && mappedOp !== "is_unknown") {
        let leafValue = node.value;
        if (mappedOp === "between") {
          if (isObject(leafValue)) leafValue = [leafValue.start, leafValue.end];
          if (!Array.isArray(leafValue) || leafValue.length < 2) return null;
        }
        if ((mappedOp === "in" || mappedOp === "not_in") && !Array.isArray(leafValue)) {
          leafValue = leafValue === undefined || leafValue === null || leafValue === "" ? [] : [leafValue];
        }
        if (leafValue === undefined) return null;
        relatedLeaf.value = leafValue;
      }

      return {
        $related: {
          object_type: relatedObjectType,
          filter: relatedLeaf,
        },
      };
    }
  }

  const field = toSafeObjectField(rawField);
  if (!field || field === "related_accounts") {
    return null;
  }

  if (rawOperator === "before" || rawOperator === "after") {
    return {
      field,
      op: rawOperator === "before" ? "lt" : "gt",
      value: toRelativeBoundaryToken(node.value, rawOperator),
    };
  }

  const mappedOperator = mapOperator(rawOperator);
  if (!mappedOperator) return null;

  if (mappedOperator === "is_known" || mappedOperator === "is_unknown") {
    return { field, op: mappedOperator };
  }

  let { value } = node;

  if (mappedOperator === "between") {
    if (isObject(value)) {
      value = [value.start, value.end];
    }
    if (!Array.isArray(value) || value.length < 2 || value[0] === undefined || value[1] === undefined) {
      return null;
    }
  }

  if (mappedOperator === "relative") {
    value = normalizeRelativeTime(value);
  }

  if ((mappedOperator === "in" || mappedOperator === "not_in") && !Array.isArray(value)) {
    value = value === undefined || value === null || value === "" ? [] : [value];
  }

  if (value === undefined) {
    return null;
  }

  return {
    field,
    op: mappedOperator,
    value,
  };
}

export const toObjectSearchFilter = (filter?: any, entityType = "") => {
  if (!isObject(filter)) return undefined;
  return toObjectFilterNode(filter, false, entityType) || undefined;
};

const mergeSelectedFields = (
  selectedFields: string[] = [],
  requiredFields: string[] = [],
) => Array.from(new Set([...(selectedFields || []), ...(requiredFields || [])]));

type BuildObjectSearchQueryParams = {
  pageIndex: number;
  pageSize: number;
  entityType?: string;
  filter?: any;
  selectedFields?: string[];
  requiredFields?: string[];
  sort?: {
    field?: string;
    order?: any;
    direction?: any;
  };
  fallbackSortField?: string;
  include?: any[];
  cursor?: string;
  includeRelatedAccountsForUsers?: boolean;
  relatedAccountsLimit?: number;
  extraQuery?: Record<string, any>;
};

const normalizeInclude = (spec: any) => {
  if (!isObject(spec)) return null;

  const objectType = normalizeObjectEntityType(spec.object_type);
  if (!objectType || !OBJECT_TYPE_PATTERN.test(objectType)) return null;

  const normalizedSelect = Array.isArray(spec.select)
    ? Array.from(new Set(spec.select
      .map((field: string) => toSafeObjectField(field))
      .filter(Boolean)))
    : [];

  const normalizedFilter = spec.filter
    ? toObjectFilterNode(spec.filter, true)
    : undefined;

  const normalizedSort = Array.isArray(spec.sort)
    ? spec.sort
      .map((sortSpec: any) => {
        const field = toSafeObjectField(sortSpec?.field);
        if (!field) return null;
        return {
          field,
          order: toObjectSortOrder(sortSpec?.order || sortSpec?.direction, "desc"),
        };
      })
      .filter(Boolean)
    : [];

  return {
    object_type: objectType,
    ...(normalizedSelect.length ? { select: normalizedSelect } : {}),
    ...(normalizedFilter ? { filter: normalizedFilter } : {}),
    ...(normalizedSort.length ? { sort: normalizedSort } : {}),
    limit: clampNumber(spec.limit, 1, 100, 10),
  };
};

export const buildObjectSearchQuery = ({
  pageIndex,
  pageSize,
  entityType,
  filter = {},
  selectedFields = [],
  requiredFields = [],
  sort,
  fallbackSortField = "",
  include = [],
  cursor,
  includeRelatedAccountsForUsers = false,
  relatedAccountsLimit = 5,
  extraQuery = {},
}: BuildObjectSearchQueryParams) => {
  const objectType: string = normalizeObjectEntityType(entityType);
  const mergedSelected = mergeSelectedFields(selectedFields, requiredFields);
  const translatedSelect = mergedSelected
    .filter((field) => !isUnsupportedObjectField(field))
    .filter((field) => field !== "related_accounts")
    .map(toSafeObjectField)
    .filter(Boolean);

  const safeFallbackSortField = !isUnsupportedObjectField(fallbackSortField)
    ? (fallbackSortField || "created_at")
    : "created_at";

  const requestedSortField = sort?.field || safeFallbackSortField || "created_at";
  const sortFieldRaw = !isUnsupportedObjectField(requestedSortField)
    ? requestedSortField
    : safeFallbackSortField;
  const sortField = toSafeObjectField(sortFieldRaw);
  const sortOrder = toObjectSortOrder(sort?.order || sort?.direction, "desc");
  const translatedFilter = toObjectSearchFilter(filter, objectType);

  const includes = [...include]
    .map((item) => normalizeInclude(item))
    .filter(Boolean);
  if (includeRelatedAccountsForUsers && objectType === "users") {
    includes.push({
      object_type: "accounts",
      select: ["name", "external_id", "avatar_url"],
      limit: clampNumber(relatedAccountsLimit, 1, 100, 10),
    });
  }

  const normalizedIncludes = includes.slice(0, 10);
  const normalizedLimit = clampNumber(pageSize, 1, 200, 25);
  const normalizedOffset = clampNumber(pageIndex * pageSize, 0, 10000, 0);

  return {
    object_type: objectType,
    ...(translatedSelect.length ? { select: Array.from(new Set(translatedSelect)) } : {}),
    ...(translatedFilter ? { filter: translatedFilter } : {}),
    sort: sortField ? [{ field: sortField, order: sortOrder }] : undefined,
    limit: normalizedLimit,
    ...((cursor && String(cursor).trim()) ? { cursor: String(cursor).trim() } : { offset: normalizedOffset }),
    ...(normalizedIncludes.length ? { include: normalizedIncludes } : {}),
    ...extraQuery,
  };
};
